import { combineReducers } from 'redux';
import { omit, mergeWith } from 'lodash-es';

import {
  ADD_COURSE_UNIT_REALISATIONS_FULFILLED,
  ADD_UNENROLLED_SURVEY_PARTICIPANTS_FULFILLED,
  ANSWER_SURVEY_QUESTION,
  CLEAR_QUESTION_RESPONSES,
  CLEAR_SURVEY,
  GET_SURVEY_FULFILLED,
  GET_SURVEY_MESSAGES_FULFILLED,
  INIT_PARTICIPANTS,
  REMOVE_COURSE_UNIT_REALISATION,
  REMOVE_SURVEY_PARTICIPANT,
  SELECT_PARTICIPANT_EMAIL_FULFILLED,
  SET_PARTICIPANT_EMAIL_FULFILLED,
  SUBMIT_SURVEY_FULFILLED,
  UPDATE_RESEARCH_CONSENT,
  UPDATE_SURVEY_OPTIONS
} from '../../actions';
import { arrayOverwritingMergeCustomizer } from '../../util';
import { participantErrors, participantWarnings } from '../../constants';

const initialSurveyOptionsState = {
  id: null,
  surveyType: null,
  courses: [],
  courseRealisationIds: [],
  participants: [],
  researchConsentNeeded: true,
  researchAgreement: null,
  extraSurveyPart: null,
  invalidFields: [],
  reminderSchedules: [],
  isValid: false
};

const isStudentParticipantInCourse = (courseId, student) =>
  (student.courseIds && student.courseIds.length === 1 && student.courseIds.includes(courseId));

const removeParticipantsInCourse = (courseId, students) => students
  .filter((s) => !isStudentParticipantInCourse(courseId, s))
  .map((s) => ({
    ...s,
    courseIds: s.courseIds && s.courseIds.filter((c) => c !== courseId)
  }));

const flattenParticipants = (participants) =>
  participants.map((student) => {
    if (student.error) {
      return { ...student, error: participantErrors.NOT_FOUND };
    }
    return { courseIds: [], ...student.result };
  });

const surveyOptions = (state = initialSurveyOptionsState, action) => {
  switch (action.type) {
    case UPDATE_SURVEY_OPTIONS: {
      return mergeWith({}, state, action.surveyOptions, arrayOverwritingMergeCustomizer);
    }
    case ADD_COURSE_UNIT_REALISATIONS_FULFILLED: {
      const isNewCourse = (course) => course.result && course.result.id
        && !state.courseRealisationIds.includes(course.result.id);

      const newCourseResults = action.payload.filter(isNewCourse);

      if (newCourseResults.length === 0) {
        return state;
      }
      const courseRealisationIds = [...state.courseRealisationIds,
        ...newCourseResults.map((course) => course.result.id)];
      const newCourses = newCourseResults.map((courseResult) => {
        const {
          result: {
            id, code, courseName, startDate, endDate, students
          }
        } = courseResult;
        return {
          id,
          code,
          courseName,
          startDate,
          endDate,
          students,
          new: true
        };
      });

      const courses = [...state.courses, ...newCourses];

      return {
        ...state, courseRealisationIds, courses
      };
    }
    case REMOVE_COURSE_UNIT_REALISATION: {
      const courseId = action.payload;

      const courses = state.courses.filter((c) => c.id !== courseId);
      const courseRealisationIds = state.courseRealisationIds.filter((c) => c !== courseId);
      const participants = removeParticipantsInCourse(courseId, state.participants);

      return {
        ...state, courses, courseRealisationIds, participants
      };
    }
    case ADD_UNENROLLED_SURVEY_PARTICIPANTS_FULFILLED: {
      const registryParticipants = action.payload;
      const currentParticipantIds = state.participants.map((p) => p.externalId);
      const newParticipants = flattenParticipants(registryParticipants).filter((participant) => (
        participant.error || !currentParticipantIds.includes(participant.externalId)
      ));
      const participants = [...state.participants, ...newParticipants.map((participant) => ({ ...participant, new: true }))];
      return participants.length ? ({ ...state, participants }) : state;
    }
    case REMOVE_SURVEY_PARTICIPANT: {
      const { idType, id } = action.payload;
      const participants = state.participants.filter((participant) => participant[idType] !== id);
      return { ...state, participants };
    }
    case SELECT_PARTICIPANT_EMAIL_FULFILLED:
    case SET_PARTICIPANT_EMAIL_FULFILLED: {
      const {
        studentNumber, warning, error, email
      } = action.payload;
      // Because user service doesn't know if the purpose was to change an existing email,
      // it still returns CHANGING_EMAIL warning if an existing user has different email.
      // In this case remove the warning.
      const participants = state.participants.map((p) => {
        if (p.studentNumber === studentNumber) {
          if (!warning || warning === participantWarnings.CHANGING_EMAIL) {
            return { ...omit(p, ['warning']), error, email };
          }
          return { ...p, error, email };
        }
        return p;
      });
      return { ...state, participants };
    }
    case GET_SURVEY_MESSAGES_FULFILLED: {
      const {
        invitationEmailTitle,
        invitationEmailContent,
        endingEmailTitle,
        endingEmailContent
      } = action.payload;
      return {
        ...state,
        invitationEmailTitle,
        invitationEmailContent,
        endingEmailTitle,
        endingEmailContent
      };
    }
    case INIT_PARTICIPANTS: {
      const { participants, courses, onlyManuallyAdded } = action.payload;
      return {
        ...state,
        participants: participants
          .filter((participant) => !onlyManuallyAdded || participant.manuallyAdded)
          .map((participant) => ({
            ...participant,
            name: participant.commonName || [participant.firstName, participant.lastName].join(' ')
          })),
        courseRealisationIds: courses.map((course) => course.courseId),
        courses: courses.map((course) => course.result || { id: course.courseId, code: course.courseId, students: [] })
      };
    }
    case CLEAR_SURVEY:
      return initialSurveyOptionsState;
    default:
      return state;
  }
};

const questionResponses = (state = {}, action) => {
  switch (action.type) {
    case ANSWER_SURVEY_QUESTION: {
      const { questionId } = action.payload;
      const { questionResponse } = action.payload;

      let newState = { ...state };

      if (questionResponse !== null) {
        newState = {
          ...state,
          [questionId]: { questionId, questionResponse }
        };
      } else {
        delete newState[questionId];
      }

      return newState;
    }
    case CLEAR_QUESTION_RESPONSES:
      return {};
    default:
      return state;
  }
};

const researchConsentGiven = (state = false, action) => {
  switch (action.type) {
    case UPDATE_RESEARCH_CONSENT:
      return action.payload;
    case CLEAR_QUESTION_RESPONSES:
      return false;
    default:
      return state;
  }
};

const surveyContent = (state = null, action) => {
  switch (action.type) {
    case GET_SURVEY_FULFILLED:
      return action.payload;
    default:
      return state;
  }
};

const surveyUrl = (state = '', action) => {
  switch (action.type) {
    case SUBMIT_SURVEY_FULFILLED:
      return action.payload.surveyUrl;
    default:
      return state;
  }
};

export default combineReducers(
  {
    surveyOptions,
    questionResponses,
    researchConsentGiven,
    surveyContent,
    surveyUrl
  }
);
